import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContractOrigin } from '../../utils/contractOrigin';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Chip,
    Button,
    Stack,
    IconButton,
    Divider,
    List,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    Paper,
    CircularProgress,
    alpha,
    Tooltip,
    Breadcrumbs,
    Link as MuiLink,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    Insights as InsightsIcon,
    ArrowBack as BackIcon,
    LocationOn as LocationIcon,
    FiberManualRecord as StatusIcon,
    Timer as TimerIcon,
    SatelliteAlt as GpsIcon,
    Map as MapIcon,
    Person,
    CalendarToday,
    Description,
    Business as BusinessIcon,
    ChevronRight as ChevronRightIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    WarningAmber as WarningAmberIcon,
    PowerSettingsNew as PowerIcon
} from '@mui/icons-material';

import mewpService from '../../services/mewpService';
import geofenceService from '../../services/geofenceService';
import { formatToIST } from '../../utils/dataUtils';
import { machineLabel } from '../../utils/machineLabel';

// Fix Leaflet marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

const ContractLiveViewPage = () => {
    const { contractId } = useParams();
    const navigate = useNavigate();
    const origin = getContractOrigin();   // the door this contract was opened from
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contract, setContract] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [alarms, setAlarms] = useState([]); // all alarms for this contract

    const fetchLiveStatus = async () => {
        try {
            const [contractRes, liveRes, alarmsRes] = await Promise.all([
                mewpService.getContractById(contractId),
                mewpService.getContractLiveStatus(contractId),
                geofenceService.listAlarms({ contract_id: contractId, limit: 100 }).catch(() => ({ data: [] }))
            ]);

            if (contractRes?.success) {
                setContract(contractRes.data);
            }

            if (liveRes?.success) {
                setDevices(liveRes.devices || []);
            }
            setAlarms(Array.isArray(alarmsRes?.data) ? alarmsRes.data : []);
            setLoading(false);
        } catch (error) {
            console.error("Failed to fetch contract live data", error);
            setLoading(false);
        }
    };

    // Close an alarm and refresh
    const handleCloseAlarm = async (alarmId) => {
        try {
            await geofenceService.closeAlarm(alarmId);
            fetchLiveStatus();
        } catch (err) {
            console.error('Failed to close alarm', err);
        }
    };

    useEffect(() => {
        fetchLiveStatus();
        const interval = setInterval(fetchLiveStatus, 30000);
        return () => clearInterval(interval);
    }, [contractId]);

    // Alarms come back from the geofence API keyed by equipment id only, so
    // name them by the serial off this contract's own machine list.
    const serialOfEquipment = useMemo(() => {
        const byEquipment = new Map(devices.map(d => [d.equipment_id, d.serial_number]));
        return (equipmentId) => byEquipment.get(equipmentId) || equipmentId || '—';
    }, [devices]);

    // Memoized filtered devices
    const filteredDevices = useMemo(() => {
        if (!searchTerm.trim()) return devices;
        const query = searchTerm.toLowerCase();
        return devices.filter(d =>
            d.serial_number?.toLowerCase().includes(query) ||
            d.equipment_id?.toLowerCase().includes(query) ||
            d.variant?.toLowerCase().includes(query) ||
            d.device_type?.toLowerCase().includes(query)
        );
    }, [devices, searchTerm]);

    // ─── Status Update: machines that haven't started on schedule ──────────
    // Rule (per requirement):
    //  • Not started today AND now is past (shift start time + 1h)  → list here
    //  • Not started for more than 1 day                            → list here
    //    AND also surface as an amber warning row in the Alarms box
    // "Started" = ignition/key ON today (computed server-side: started_today).
    // shift_start_time comes from the contract equipment; falls back to 09:00.
    const { statusUpdates, idleWarnings } = useMemo(() => {
        const now = new Date();
        const todayMidnight = new Date(now);
        todayMidnight.setHours(0, 0, 0, 0);

        const list = [];
        const warnings = [];

        // Guard: if the backend doesn't yet provide start-tracking fields
        // (older API build), skip flagging to avoid false "not started" rows.
        const hasStartData = devices.some(d => Object.prototype.hasOwnProperty.call(d, 'started_today'));
        if (!hasStartData) return { statusUpdates: list, idleWarnings: warnings };

        devices.forEach(d => {
            if (d.started_today) return; // already started today — nothing to flag

            const shift = (d.shift_start_time && /^\d{1,2}:\d{2}/.test(d.shift_start_time))
                ? d.shift_start_time
                : '09:00:00';
            const [sh, sm] = shift.split(':').map(Number);
            const gate = new Date(todayMidnight);
            gate.setHours(sh, sm, 0, 0);
            gate.setHours(gate.getHours() + 1); // start time + 1 hour grace

            let daysIdle = null;
            if (d.last_start) {
                const ls = new Date(d.last_start);
                ls.setHours(0, 0, 0, 0);
                daysIdle = Math.round((todayMidnight - ls) / 86400000);
            }
            // >1 day = last started 2+ calendar days ago, or no start within 30-day lookback
            const moreThanOneDay = d.last_start ? daysIdle >= 2 : true;

            const item = {
                equipment_id: d.equipment_id,
                serial_number: d.serial_number,
                variant: d.variant,
                device_type: d.device_type,
                last_start: d.last_start,
                shift_start_time: shift,
                daysIdle,
                moreThanOneDay
            };

            if (moreThanOneDay) {
                list.push(item);
                warnings.push(item);
            } else if (now >= gate) {
                list.push(item); // not started today and past the grace window
            }
        });

        // Most idle first
        list.sort((a, b) => (b.daysIdle ?? 9999) - (a.daysIdle ?? 9999));
        return { statusUpdates: list, idleWarnings: warnings };
    }, [devices]);

    const getStatusColor = (status) => {
        const s = status?.toUpperCase();
        if (s === 'ON' || s === 'ACTIVE' || s === 'KEY_ON' || s === 'IGNITION') return '#10b981'; // Green
        if (s === 'GPS ON' || s === 'IDLE') return '#f59e0b'; // Amber
        if (s === 'OFF' || s === 'KEY_OFF' || s === 'OFFLINE') return '#64748b'; // Slate
        return '#94a3b8';
    };

    if (loading && !contract) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // Determine Map Center
    const mapCenter = filteredDevices.find(d => d.location?.lat)?.location
        ? [parseFloat(filteredDevices.find(d => d.location?.lat).location.lat), parseFloat(filteredDevices.find(d => d.location?.lat).location.lon)]
        : [20.5937, 78.9629];

    return (
        <Box p={3} sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header Navigation */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <IconButton onClick={() => navigate(origin.path)} sx={{ bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} aria-label={`Back to ${origin.label}`}>
                    <BackIcon />
                </IconButton>
                <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />}>
                    <MuiLink component="button" onClick={() => navigate(origin.path)} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>{origin.label}</MuiLink>
                    {/* The site is what the person recognises; the contract id rides beside it. */}
                    <Typography color="text.primary" sx={{ fontWeight: 700 }}>
                        {contract?.site_location ? `${contract.site_location} · ${contractId}` : contractId}
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Top Bar: Contract Details */}
            <Card sx={{ mb: 4, borderRadius: '24px', overflow: 'visible' }}>
                <CardContent sx={{ p: '24px !important' }}>
                    <Box display="flex" alignItems="center" width="100%" sx={{ gap: 3 }}>

                        {/* 1. Contract Identity */}
                        <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 280 }}>
                            <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', borderRadius: '12px', width: 56, height: 56 }} variant="rounded">
                                <BusinessIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{contractId}</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                    {contract?.company_name || contract?.customer || 'Rental Contract'}
                                </Typography>
                                <Chip
                                    label={`${devices.length} Units Active`}
                                    color="primary"
                                    size="small"
                                    sx={{ fontWeight: 800, borderRadius: '6px', height: '22px', fontSize: '11px' }}
                                />
                            </Box>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ opacity: 0.6 }} />

                        {/* 2. Contract Stats Row */}
                        <Box flexGrow={1} display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: 2 }}>

                            {/* Start Date */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'success.50', color: 'success.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <CalendarToday sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">START DATE</Typography>
                                    <Typography variant="subtitle2" fontWeight={800}>{contract?.start_date ? new Date(contract.start_date).toLocaleDateString() : '-'}</Typography>
                                </Box>
                            </Box>

                            {/* Assigned To */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <Person sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">ASSIGNED TO</Typography>
                                    <Typography variant="subtitle2" fontWeight={800}>{contract?.contact_person || 'david'}</Typography>
                                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>{contract?.company_name || 'Elite Enterprises'}</Typography>
                                </Box>
                            </Box>

                            {/* Contact Info */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'info.50', color: 'info.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <Person sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">CONTACT INFO</Typography>
                                    <Typography variant="subtitle2" fontWeight={800}>{contract?.contact_phone || "9840080447"}</Typography>
                                </Box>
                            </Box>

                            {/* Site Location */}
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: 'error.50', color: 'error.main', borderRadius: '8px', width: 36, height: 36 }} variant="rounded">
                                    <LocationIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">LOCATION</Typography>
                                    <Tooltip title={contract?.site_location}>
                                        <Typography variant="subtitle2" fontWeight={800} sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {contract?.site_location || 'LG Electronics I...'}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            </Box>

                        </Box>

                        <Box display="flex" alignItems="center" gap={1} sx={{ pl: 2, borderLeft: 1, borderColor: 'divider' }}>
                            <Tooltip title="Usage summary — machines worked, running / extra / engine hours by day, week or month">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<InsightsIcon sx={{ fontSize: 18 }} />}
                                    onClick={() => navigate(`/contract-summary/${contractId}`)}
                                    sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap' }}
                                >
                                    Summary
                                </Button>
                            </Tooltip>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Description sx={{ fontSize: 18 }} />}
                                onClick={() => navigate(`/contract/${contractId}`)}
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    px: 2,
                                    py: 1,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }}
                            >
                                View Contract
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Box display="flex" flexWrap="wrap" gap={3} sx={{ mb: 3 }}>
                {/* LEFT SIDE: MAP — flexes to fill whatever width the constant-width list leaves behind */}
                <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 0%' }, minWidth: 0 }}>
                    <Card sx={{
                        height: '600px',
                        width: '100%',
                        borderRadius: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                    }}>
                        <CardHeader
                            avatar={<Avatar sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider', width: 32, height: 32 }}><MapIcon sx={{ fontSize: 18, color: 'text.primary' }} /></Avatar>}
                            title="Live Site Map"
                            titleTypographyProps={{ fontWeight: 800 }}
                            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
                        />
                        <Box sx={{ flexGrow: 1, position: 'relative' }}>
                            <MapContainer
                                center={mapCenter}
                                zoom={filteredDevices.find(d => d.location?.lat) ? 15 : 5}
                                style={{ height: '100%', width: '100%' }}
                                key={filteredDevices.length > 0 ? filteredDevices[0].equipment_id : 'default'}
                            >
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
                                {filteredDevices.map((device) => {
                                    const lat = parseFloat(device.location?.lat);
                                    const lon = parseFloat(device.location?.lon);
                                    if (!isNaN(lat) && !isNaN(lon) && lat !== 0) {
                                        return (
                                            <Marker
                                                key={device.equipment_id}
                                                position={[lat, lon]}
                                                icon={L.divIcon({
                                                    className: 'custom-div-icon',
                                                    html: `<div style="background-color: ${getStatusColor(device.status)}; width: 14px; height: 14px; border: 2.5px solid white; border-radius: 50%; box-shadow: 0 0 10px rgba(0,0,0,0.4);"></div>`,
                                                    iconSize: [14, 14],
                                                    iconAnchor: [7, 7]
                                                })}
                                            >
                                                <Popup>
                                                    <Box p={1}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{machineLabel(device)}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{device.variant}</Typography>
                                                        <Divider sx={{ my: 1 }} />
                                                        <Stack spacing={0.5}>
                                                            <Box display="flex" justifyContent="space-between" gap={2}>
                                                                <Typography variant="caption">Status:</Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: getStatusColor(device.status) }}>{device.status}</Typography>
                                                            </Box>
                                                            <Box display="flex" justifyContent="space-between" gap={2}>
                                                                <Typography variant="caption">Hours:</Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700 }}>{device.engine_hours}h</Typography>
                                                            </Box>
                                                        </Stack>
                                                        <Button
                                                            fullWidth
                                                            size="small"
                                                            variant="contained"
                                                            sx={{ mt: 1.5, textTransform: 'none', borderRadius: '8px' }}
                                                            onClick={() => navigate(`/rental-details/${device.equipment_id}?contractId=${contractId}`)}
                                                        >
                                                            Analytics
                                                        </Button>
                                                    </Box>
                                                </Popup>
                                            </Marker>
                                        );
                                    }
                                    return null;
                                })}
                            </MapContainer>
                        </Box>
                    </Card>
                </Box>

                {/* RIGHT SIDE: EQUIPMENT LIST — constant 400px, never shrinks/grows with viewport */}
                <Box sx={{ flex: { xs: '1 1 100%', lg: '0 0 400px' }, width: { xs: '100%', lg: '400px' } }}>
                    <Card sx={{
                        height: '600px',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '24px',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
                    }}>
                        <CardHeader
                            title="Site Equipment List"
                            titleTypographyProps={{ fontWeight: 800 }}
                            action={
                                <TextField
                                    size="small"
                                    placeholder="Search Unit / Model..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{
                                        width: 200,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            bgcolor: 'white'
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                    <CloseIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            }
                            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
                        />
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#fcfcfc' }}>
                            {filteredDevices.length === 0 ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" color="text.secondary">
                                    <SearchIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
                                    <Typography variant="body2">No units found matching "{searchTerm}"</Typography>
                                </Box>
                            ) : (
                                <List disablePadding>
                                    {filteredDevices.map((device) => (
                                        <Paper key={device.equipment_id} elevation={0} sx={{
                                            mb: 2,
                                            borderRadius: '16px',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            overflow: 'hidden',
                                            transition: 'all 0.2s',
                                            '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
                                        }}>
                                            <ListItemButton
                                                onClick={() => navigate(`/rental-details/${device.equipment_id}?contractId=${contractId}`)}
                                                sx={{ p: 2, alignItems: 'flex-start' }}
                                            >
                                                <ListItemAvatar sx={{ minWidth: 56 }}>
                                                    <Avatar sx={{
                                                        bgcolor: alpha(getStatusColor(device.status), 0.1),
                                                        color: getStatusColor(device.status),
                                                        borderRadius: '12px',
                                                        width: 44,
                                                        height: 44
                                                    }}>
                                                        {device.status?.includes('GPS') ? <GpsIcon /> : <InsightsIcon />}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                                                            {machineLabel(device)}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Stack spacing={0.5}>
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                                {device.variant} • {device.device_type}
                                                            </Typography>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <TimerIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                                    {device.engine_hours} Engine Hours
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    }
                                                />
                                                <Stack alignItems="flex-end" spacing={1}>
                                                    <Chip
                                                        icon={<StatusIcon sx={{ fontSize: '10px !important' }} />}
                                                        label={device.status || 'OFFLINE'}
                                                        size="small"
                                                        sx={{
                                                            height: 24,
                                                            fontSize: '10px',
                                                            fontWeight: 900,
                                                            bgcolor: alpha(getStatusColor(device.status), 0.1),
                                                            color: getStatusColor(device.status),
                                                            border: `1px solid ${alpha(getStatusColor(device.status), 0.2)}`,
                                                            '& .MuiChip-icon': { color: 'inherit' }
                                                        }}
                                                    />
                                                </Stack>
                                            </ListItemButton>
                                        </Paper>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </Card>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* ─── Alarms on this Rental ────────────────────────────── */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', height: '100%' }}>
                        <CardHeader
                            avatar={
                                <Avatar sx={{ bgcolor: '#fef2f2', color: '#dc2626', borderRadius: '12px', width: 36, height: 36 }} variant="rounded">
                                    <StatusIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                            }
                            title={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Alarms on this Rental</Typography>
                                    {alarms.filter(a => a.alarm_status === 'OPEN').length > 0 && (
                                        <Chip
                                            label={`${alarms.filter(a => a.alarm_status === 'OPEN').length} OPEN`}
                                            size="small"
                                            sx={{ bgcolor: '#dc2626', color: 'white', fontWeight: 800, height: 22, fontSize: '11px' }}
                                        />
                                    )}
                                    {idleWarnings.length > 0 && (
                                        <Chip
                                            icon={<WarningAmberIcon sx={{ fontSize: '12px !important' }} />}
                                            label={`${idleWarnings.length} IDLE >1 DAY`}
                                            size="small"
                                            sx={{ bgcolor: '#f59e0b', color: 'white', fontWeight: 800, height: 22, fontSize: '11px', '& .MuiChip-icon': { color: 'white' } }}
                                        />
                                    )}
                                </Box>
                            }
                            subheader={
                                <Typography variant="caption" color="text.secondary">
                                    Geofence and machine alarms triggered for any equipment on contract {contractId}.
                                </Typography>
                            }
                            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
                        />
                        <Box p={2} sx={{ maxHeight: 360, overflowY: 'auto' }}>
                            {alarms.length === 0 && idleWarnings.length === 0 ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5} color="text.secondary">
                                    <StatusIcon sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
                                    <Typography variant="body2" fontWeight={600}>No alarms for this rental — all clear.</Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.2}>
                                    {/* Idle >1 day machines surfaced here as amber warnings */}
                                    {idleWarnings.map(w => (
                                        <Paper
                                            key={`idle-warn-${w.equipment_id}`}
                                            elevation={0}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: '12px',
                                                border: '1px solid',
                                                borderLeft: '4px solid #f59e0b',
                                                borderColor: 'divider',
                                                bgcolor: '#fffbeb',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2
                                            }}
                                        >
                                            <WarningAmberIcon sx={{ color: '#f59e0b' }} />
                                            <Box flexGrow={1} minWidth={0}>
                                                <Box display="flex" alignItems="center" gap={1} mb={0.3} flexWrap="wrap">
                                                    <Chip
                                                        label="NOT STARTED"
                                                        size="small"
                                                        sx={{ fontWeight: 800, height: 20, fontSize: '10px', bgcolor: '#f59e0b', color: 'white' }}
                                                    />
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                        {machineLabel(w)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                    Machine has not started for {w.daysIdle != null ? `${w.daysIdle} days` : 'over 30 days'}.
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                                    Last start: {w.last_start ? formatToIST(w.last_start, 'dd MMM, hh:mm a') : 'No recent start'}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    ))}
                                    {alarms.map(a => {
                                        const isOpen = a.alarm_status === 'OPEN';
                                        return (
                                            <Paper
                                                key={a.id}
                                                elevation={0}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: '12px',
                                                    border: '1px solid',
                                                    borderLeft: `4px solid ${isOpen ? '#dc2626' : '#10b981'}`,
                                                    borderColor: 'divider',
                                                    bgcolor: isOpen ? '#fef2f2' : '#f0fdf4',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2
                                                }}
                                            >
                                                <Box flexGrow={1} minWidth={0}>
                                                    <Box display="flex" alignItems="center" gap={1} mb={0.3} flexWrap="wrap">
                                                        <Chip
                                                            label={a.alarm_name}
                                                            size="small"
                                                            sx={{ fontWeight: 800, height: 20, fontSize: '10px',
                                                                  bgcolor: isOpen ? '#dc2626' : '#10b981', color: 'white' }}
                                                        />
                                                        <Chip
                                                            label={a.category}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontWeight: 700, height: 20, fontSize: '10px' }}
                                                        />
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                            {serialOfEquipment(a.equipment_id)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                        {a.message || (a.alarm_name === 'GEOFENCE_EXIT'
                                                            ? `Equipment left the site geofence.`
                                                            : '')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                                        Opened: {a.created_at ? formatToIST(a.created_at, 'dd MMM, hh:mm:ss a') : '-'}
                                                        {a.closed_at && <> · Closed: {formatToIST(a.closed_at, 'dd MMM, hh:mm:ss a')}</>}
                                                    </Typography>
                                                </Box>
                                                {isOpen && (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() => handleCloseAlarm(a.id)}
                                                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', whiteSpace: 'nowrap' }}
                                                    >
                                                        Close
                                                    </Button>
                                                )}
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    </Card>
                </Grid>

                {/* ─── Status Update (machine start tracking) ───────────────── */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', height: '100%' }}>
                        <CardHeader
                            avatar={
                                <Avatar sx={{ bgcolor: '#fffbeb', color: '#f59e0b', borderRadius: '12px', width: 36, height: 36 }} variant="rounded">
                                    <PowerIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                            }
                            title={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Status Update</Typography>
                                    {statusUpdates.length > 0 && (
                                        <Chip
                                            label={`${statusUpdates.length} NOT STARTED`}
                                            size="small"
                                            sx={{ bgcolor: '#f59e0b', color: 'white', fontWeight: 800, height: 22, fontSize: '11px' }}
                                        />
                                    )}
                                </Box>
                            }
                            subheader={
                                <Typography variant="caption" color="text.secondary">
                                    Machines not started today (past start time + 1h) or idle for more than a day.
                                </Typography>
                            }
                            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
                        />
                        <Box p={2} sx={{ maxHeight: 360, overflowY: 'auto' }}>
                            {statusUpdates.length === 0 ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5} color="text.secondary">
                                    <PowerIcon sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
                                    <Typography variant="body2" fontWeight={600}>All machines started on schedule.</Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.2}>
                                    {statusUpdates.map(s => {
                                        const severe = s.moreThanOneDay;
                                        const color = severe ? '#dc2626' : '#f59e0b';
                                        const bg = severe ? '#fef2f2' : '#fffbeb';
                                        return (
                                            <Paper
                                                key={`status-${s.equipment_id}`}
                                                elevation={0}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: '12px',
                                                    border: '1px solid',
                                                    borderLeft: `4px solid ${color}`,
                                                    borderColor: 'divider',
                                                    bgcolor: bg,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2
                                                }}
                                            >
                                                <WarningAmberIcon sx={{ color }} />
                                                <Box flexGrow={1} minWidth={0}>
                                                    <Box display="flex" alignItems="center" gap={1} mb={0.3} flexWrap="wrap">
                                                        <Chip
                                                            label={severe ? `IDLE ${s.daysIdle != null ? s.daysIdle + ' DAYS' : '30+ DAYS'}` : 'NOT STARTED TODAY'}
                                                            size="small"
                                                            sx={{ fontWeight: 800, height: 20, fontSize: '10px', bgcolor: color, color: 'white' }}
                                                        />
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                            {machineLabel(s)}
                                                        </Typography>
                                                        {s.variant && (
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                                {s.variant}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                        {severe
                                                            ? `Not started for ${s.daysIdle != null ? s.daysIdle + ' days' : 'over 30 days'}.`
                                                            : `Not started today — shift began at ${s.shift_start_time?.slice(0, 5)}.`}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                                        Last start: {s.last_start ? formatToIST(s.last_start, 'dd MMM, hh:mm a') : 'No recent start'}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    </Card>
                </Grid>
            </Grid>

        </Box>
    );
};

export default ContractLiveViewPage;
